import { ICONS } from './commentComposerIcons.js';
import { selectionInsideEditor, syncToolbarState } from './commentComposerToolbar.js';

type FormatCommand = 'bold' | 'italic' | 'insertOrderedList';

type SpeechRecognitionResultLike = {
  isFinal?: boolean;
  0?: { transcript?: string };
};

type SpeechRecognitionEventLike = {
  resultIndex?: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionErrorEventLike = {
  error?: string;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: null | (() => void);
  onerror: null | ((event: SpeechRecognitionErrorEventLike) => void);
  onresult: null | ((event: SpeechRecognitionEventLike) => void);
  start(): void;
  stop(): void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export interface CommentComposerHandle {
  destroy(): void;
  focus(): void;
  getAttachment(): File | null;
  getHtml(): string;
  getPlainText(): string;
  isEmpty(): boolean;
  setError(message: string): void;
  stopVoiceInput(): void;
}

interface CommentComposerOptions {
  container: HTMLElement;
  onSubmit(): void;
}

const FORMAT_BUTTONS: ReadonlyArray<{
  command: FormatCommand;
  label: string;
  title: string;
  icon: string;
}> = [
  {
    command: 'bold',
    label: 'Bold',
    title: 'Bold',
    icon: ICONS.bold,
  },
  {
    command: 'italic',
    label: 'Italic',
    title: 'Italic',
    icon: ICONS.italic,
  },
  {
    command: 'insertOrderedList',
    label: 'List',
    title: 'Ordered list',
    icon: ICONS.orderedList,
  },
];

export function renderCommentBoxHtml(): string {
  const formatButtons = FORMAT_BUTTONS.map(
    ({ command, label, title, icon }) => `
      <button class="bs-fmt-btn" type="button" data-command="${command}" aria-label="${label}" title="${title}">
        ${icon}
      </button>`,
  ).join('');

  return `
    <div class="bs-editor-area">
      <div
        class="bs-editor"
        contenteditable="true"
        role="textbox"
        aria-label="Add feedback here"
        aria-multiline="true"
      ></div>
    </div>
    <div class="bs-divider"></div>
    <div class="bs-toolbar">
      <div class="bs-toolbar-left">
        ${formatButtons}
        <label class="bs-fmt-btn bs-attach-btn" aria-label="Attach image" title="Upload file">
          ${ICONS.paperclip}
          <input class="bs-attachment-input" type="file" accept="image/*" />
        </label>
        <div class="bs-attachment-preview" hidden>
          <img class="bs-attachment-img" alt="" />
          <button class="bs-remove-attachment" type="button" aria-label="Remove attachment">${ICONS.close}</button>
        </div>
      </div>
      <div class="bs-toolbar-right">
        <button class="bs-mic-btn" type="button" aria-label="Start voice dictation" title="Voice dictation">
          ${ICONS.microphone}
        </button>
        <button class="bs-submit-btn" type="button" aria-label="Submit feedback">
          ${ICONS.send}
        </button>
      </div>
    </div>
    <div id="bs-err" class="bs-error" style="display:none"></div>
  `;
}

export function setupCommentComposer(
  options: CommentComposerOptions,
): CommentComposerHandle {
  const { container, onSubmit } = options;
  const editor = queryRequired<HTMLDivElement>(container, '.bs-editor');
  const submitButton = queryRequired<HTMLButtonElement>(container, '.bs-submit-btn');
  const micButton = queryRequired<HTMLButtonElement>(container, '.bs-mic-btn');
  const errorElement = queryRequired<HTMLDivElement>(container, '#bs-err');
  const attachment = setupAttachmentControls(container, errorElement);
  const commandButtons = Array.from(
    container.querySelectorAll<HTMLButtonElement>('.bs-fmt-btn[data-command]'),
  );
  const syncToolbar = () => syncToolbarState(editor, commandButtons);
  const clearError = () => setComposerError(errorElement, '');
  const voiceInput = createVoiceInputControls({ clearError, editor, errorElement, micButton });

  bindEditorEvents({ clearError, editor, onSubmit, syncToolbar });
  bindCommandButtons({ buttons: commandButtons, clearError, editor, syncToolbar });
  submitButton.addEventListener('click', onSubmit);
  micButton.addEventListener('click', voiceInput.toggleVoiceInput);

  const selectionListener = () => syncToolbar();
  document.addEventListener('selectionchange', selectionListener);

  normalizeEditorMarkup(editor);
  focusEditorAtEnd(editor);
  syncToolbar();

  return {
    destroy() {
      attachment.destroy();
      voiceInput.stopVoiceInput();
      document.removeEventListener('selectionchange', selectionListener);
    },
    focus() {
      focusEditorAtEnd(editor);
      syncToolbar();
    },
    getAttachment() {
      return attachment.getAttachment();
    },
    getHtml() {
      return getEditorHtml(editor);
    },
    getPlainText() {
      return getEditorPlainText(editor);
    },
    isEmpty() {
      return isEditorEmpty(editor);
    },
    setError(message: string) {
      setComposerError(errorElement, message);
    },
    stopVoiceInput: voiceInput.stopVoiceInput,
  };
}

function bindEditorEvents(options: {
  clearError(): void;
  editor: HTMLElement;
  onSubmit(): void;
  syncToolbar(): void;
}) {
  const { clearError, editor, onSubmit, syncToolbar } = options;
  editor.addEventListener('input', () => {
    clearError();
    normalizeEditorMarkup(editor);
    syncToolbar();
  });
  editor.addEventListener('keydown', (event) => handleEditorKeyDown(event, onSubmit));
  editor.addEventListener('keyup', syncToolbar);
  editor.addEventListener('mouseup', syncToolbar);
}

function bindCommandButtons(options: {
  buttons: HTMLButtonElement[];
  clearError(): void;
  editor: HTMLElement;
  syncToolbar(): void;
}) {
  const { buttons, clearError, editor, syncToolbar } = options;
  for (const button of buttons) {
    button.addEventListener('mousedown', (event) => event.preventDefault());
    button.addEventListener('click', () => {
      clearError();
      applyFormatCommand(editor, button.dataset.command as FormatCommand);
      syncToolbar();
    });
  }
}

function setupAttachmentControls(container: HTMLElement, errorElement: HTMLElement) {
  const input = queryRequired<HTMLInputElement>(container, '.bs-attachment-input');
  const preview = queryRequired<HTMLDivElement>(container, '.bs-attachment-preview');
  const image = queryRequired<HTMLImageElement>(container, '.bs-attachment-img');
  const removeButton = queryRequired<HTMLButtonElement>(container, '.bs-remove-attachment');
  let attachment: File | null = null;
  let previewUrl: string | null = null;

  const clearAttachment = () => {
    attachment = null;
    input.value = '';
    input.parentElement!.hidden = false;
    preview.hidden = true;
    image.removeAttribute('src');
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = null;
  };

  input.addEventListener('change', () => {
    const file = input.files?.[0] ?? null;
    if (!file) return clearAttachment();
    if (!file.type.startsWith('image/')) {
      setComposerError(errorElement, 'Please choose an image attachment.');
      return clearAttachment();
    }
    attachment = file;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = URL.createObjectURL(file);
    image.src = previewUrl;
    input.parentElement!.hidden = true;
    preview.hidden = false;
    setComposerError(errorElement, '');
  });
  removeButton.addEventListener('click', clearAttachment);

  return {
    destroy: clearAttachment,
    getAttachment: () => attachment,
  };
}

function createVoiceInputControls(options: {
  clearError(): void;
  editor: HTMLElement;
  errorElement: HTMLElement;
  micButton: HTMLButtonElement;
}) {
  const { clearError, editor, errorElement, micButton } = options;
  let recognition: SpeechRecognitionLike | null = null;
  let listening = false;

  const stopVoiceInput = () => {
    if (!listening || !recognition) return;
    recognition.stop();
    listening = false;
    updateMicState(micButton, false);
  };

  const startVoiceInput = () => {
    clearError();
    recognition = createSpeechRecognition({
      editor,
      onError: (message) => setComposerError(errorElement, message),
      onListeningChange: (isListening) => {
        listening = isListening;
        updateMicState(micButton, isListening);
      },
    });
    if (!recognition) {
      setComposerError(errorElement, 'Voice dictation is not supported in this browser.');
      return;
    }
    recognition.start();
  };

  return {
    stopVoiceInput,
    toggleVoiceInput: () => (listening ? stopVoiceInput() : startVoiceInput()),
  };
}

function queryRequired<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing composer element: ${selector}`);
  }

  return element;
}

function handleEditorKeyDown(event: KeyboardEvent, onSubmit: () => void) {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    event.preventDefault();
    onSubmit();
  }
}

function applyFormatCommand(editor: HTMLElement, command: FormatCommand) {
  const hadContent = !isEditorEmpty(editor);
  ensureSelectionInEditor(editor);
  document.execCommand(command, false);
  if (hadContent || !isEditorEmpty(editor)) {
    normalizeEditorMarkup(editor);
  }
}

function ensureSelectionInEditor(editor: HTMLElement) {
  if (selectionInsideEditor(editor)) editor.focus();
  else focusEditorAtEnd(editor);
}

function focusEditorAtEnd(editor: HTMLElement) {
  editor.focus();
  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function setComposerError(errorElement: HTMLElement, message: string) {
  errorElement.textContent = message;
  errorElement.style.display = message ? 'block' : 'none';
}

function normalizeEditorMarkup(editor: HTMLElement) {
  if (isEditorEmpty(editor)) {
    editor.innerHTML = '';
  }
}

function getEditorHtml(editor: HTMLElement) {
  if (isEditorEmpty(editor)) return '';

  const rawHtml = editor.innerHTML.trim();
  if (containsHtmlTag(rawHtml)) return rawHtml;

  return `<p>${escapeHtml(getEditorPlainText(editor))}</p>`;
}

function getEditorPlainText(editor: HTMLElement) {
  return (editor.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

function isEditorEmpty(editor: HTMLElement) {
  return getEditorPlainText(editor).length === 0;
}

function containsHtmlTag(value: string) {
  return /<([a-z][^/\s>]*)[\s>]/i.test(value);
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function createSpeechRecognition(options: {
  editor: HTMLElement;
  onError(message: string): void;
  onListeningChange(isListening: boolean): void;
}) {
  const Recognition = getSpeechRecognitionCtor();
  if (!Recognition) return null;

  const recognition = new Recognition();
  recognition.continuous = true;
  recognition.interimResults = false;
  recognition.lang = navigator.language || 'en-US';
  recognition.onresult = (event: SpeechRecognitionEventLike) => {
    const transcript = extractTranscript(event);
    if (transcript) {
      appendTranscript(options.editor, transcript);
    }
  };
  recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
    options.onError(getSpeechErrorMessage(event.error));
    options.onListeningChange(false);
  };
  recognition.onend = () => {
    options.onListeningChange(false);
  };

  const originalStart = recognition.start.bind(recognition);
  recognition.start = () => {
    try {
      options.onListeningChange(true);
      originalStart();
    } catch {
      options.onListeningChange(false);
      options.onError('Voice dictation could not start. Please try again.');
    }
  };

  return recognition;
}

function getSpeechRecognitionCtor() {
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  const candidate = speechWindow.webkitSpeechRecognition ?? speechWindow.SpeechRecognition;

  return typeof candidate === 'function' ? candidate : null;
}

function extractTranscript(event: SpeechRecognitionEventLike) {
  const parts: string[] = [];
  const startIndex = event.resultIndex ?? 0;

  for (let index = startIndex; index < event.results.length; index += 1) {
    const result = event.results[index];
    if (!result) continue;
    if (result.isFinal === false) continue;
    const transcript = result[0]?.transcript?.trim();
    if (transcript) parts.push(transcript);
  }

  return parts.join(' ').trim();
}

function appendTranscript(editor: HTMLElement, transcript: string) {
  const selection = window.getSelection();
  const textNode = document.createTextNode(`${isEditorEmpty(editor) ? '' : ' '}${transcript} `);

  if (selection && selection.rangeCount > 0 && selection.anchorNode && editor.contains(selection.anchorNode)) {
    const range = selection.getRangeAt(0);
    range.collapse(false);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    editor.append(textNode);
    focusEditorAtEnd(editor);
  }

  normalizeEditorMarkup(editor);
}

function updateMicState(button: HTMLButtonElement, listening: boolean) {
  button.classList.toggle('is-recording', listening);
  button.setAttribute('aria-label', listening ? 'Stop voice dictation' : 'Start voice dictation');
  button.title = listening ? 'Stop voice dictation' : 'Voice dictation';
}

export function getSubmitIcon() {
  return ICONS.send;
}

function getSpeechErrorMessage(error?: string) {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was denied. Please allow microphone access and try again.';
    case 'no-speech':
      return 'No speech was detected. Please try again.';
    case 'audio-capture':
      return 'No microphone was found for voice dictation.';
    default:
      return 'Voice dictation failed. Please try again.';
  }
}
